// src/components/ExcelExport.jsx
// Requires: npm i xlsx-js-style fflate
import * as XLSX from "xlsx-js-style";
import { unzipSync, zipSync, strToU8, strFromU8 } from "fflate";

// Red, minus-sign negatives (no parentheses), accounting-aligned
const ACCT = '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';
const PCT = "0.00%;[Red]-0.00%";
const HITQUOTA = "DCFCE7"; // soft green — rows that hit >=99% of quota
const QUOTA_HILITE = 0.99; // highlight threshold (quotaAchieved is a fraction)

// ── Sizing (bigger fonts + taller rows; print stays width-fit) ──────
const FONT_SZ = { band: 12, head: 11, data: 12, total: 12 };
const ROW_HPT = { band: 26, head: 34, data: 22, spacer: 26, total: 24 };

// ── Store divisions ────────────────────────────────────────────────
const EE_STORES = new Set([1, 2, 3, 9, 15, 18, 19, 21, 22]);
const GE_STORES = new Set([4, 5, 6, 7, 8, 10, 11, 12, 14, 16, 17, 23]);

// ── Group colors ───────────────────────────────────────────────────
const GROUP_META = {
  Identity: { band: "E2E8F0", head: "F1F5F9", text: "334155" },
  Total: { band: "FEF3C7", head: "FFFBEB", text: "92400E" },
  "Store Performance": { band: "DBEAFE", head: "EFF6FF", text: "1E40AF" },
  "Store Commission": { band: "EDE9FE", head: "F5F3FF", text: "5B21B6" },
  "Salesman Commission": { band: "CFFAFE", head: "ECFEFF", text: "155E75" },
};

// ── Column definitions ─────────────────────────────────────────────
const COLUMNS = [
  {
    key: "store",
    group: "Identity",
    type: "text",
    center: true,
    width: 6,
    header: () => "Store",
    value: (s) => `${s.storeId}`,
  },
  {
    key: "name",
    group: "Identity",
    type: "text",
    width: 32,
    header: (role) => role,
    value: (s) => (s.name ? `${s.name} (${s.employeeId})` : s.employeeId || ""),
  },
  {
    key: "pbsId",
    group: "Identity",
    type: "text",
    center: true,
    width: 12,
    header: () => "PBS ID",
    value: (s) => s.pbsId || "—",
  },
  {
    key: "total",
    group: "Total",
    type: "cur",
    width: 16,
    totalKey: "total",
    header: () => "Total Comm.",
    value: (s) => Number(s.totalCommission) || 0,
  },
  {
    key: "storeSale",
    group: "Store Performance",
    type: "cur",
    width: 18,
    totalKey: "storeSale",
    header: () => "Store Sales",
    value: (s) => Number(s.storeSale) || 0,
  },
  {
    key: "quota",
    group: "Store Performance",
    type: "cur",
    width: 16,
    totalKey: "quota",
    header: () => "Quota",
    value: (s) => Number(s.quota) || 0,
  },
  {
    key: "quotaPct",
    group: "Store Performance",
    type: "pct",
    center: true,
    width: 12,
    totalKey: "quotaPct",
    header: () => "Quota %",
    value: (s) => (s.quotaAchieved != null ? Number(s.quotaAchieved) : null),
  },
  {
    key: "upDown",
    group: "Store Performance",
    type: "pct",
    center: true,
    width: 12,
    totalKey: "upDown",
    header: () => "Up/Down %",
    value: (s) =>
      s.storeUpDownPercent != null ? Number(s.storeUpDownPercent) : null,
  },
  {
    key: "storeRate",
    group: "Store Commission",
    type: "pct",
    center: true,
    width: 14,
    employeeHide: true,
    header: () => "Store Credit Rate",
    value: (s) => Number(s.storeCreditRate) || 0,
  },
  {
    key: "storeComm",
    group: "Store Commission",
    type: "cur",
    width: 16,
    employeeHide: true,
    totalKey: "storeComm",
    header: () => "Store Comm.",
    value: (s) => Number(s.storeCreditAmt) || 0,
  },
  {
    key: "salesmanSale",
    group: "Salesman Commission",
    type: "cur",
    width: 18,
    totalKey: "salesmanSale",
    header: () => "Salesman Sales",
    value: (s) => Number(s.salesmanSale) || 0,
  },
  {
    key: "salesmanRate",
    group: "Salesman Commission",
    type: "pct",
    center: true,
    width: 13,
    header: () => "Salesman Rate",
    value: (s) => Number(s.salesmanRate) || 0,
  },
  {
    key: "salesmanComm",
    group: "Salesman Commission",
    type: "cur",
    width: 18,
    totalKey: "salesmanComm",
    header: () => "Salesman Comm.",
    value: (s) => Number(s.salesmanCommissionAmt) || 0,
  },
];

// Write the workbook, then patch EVERY sheet's XML for landscape print and tab color.
function downloadLandscapeXlsx(wb, filename) {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const files = unzipSync(new Uint8Array(buf));

  Object.keys(files)
    .filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k))
    .forEach((sheetPath) => {
      const match = sheetPath.match(/^xl\/worksheets\/sheet(\d+)\.xml$/);
      let tabColor = null;
      if (match && wb.tabColors) {
        const idx = parseInt(match[1], 10) - 1;
        tabColor = wb.tabColors[idx];
      }

      let xml = strFromU8(files[sheetPath]);
      const tabColorXml = tabColor ? `<tabColor rgb="${tabColor}"/>` : "";

      if (xml.includes("<sheetPr")) {
        if (!xml.includes("pageSetUpPr")) {
          xml = xml.replace(
            /(<sheetPr[^>]*>)/,
            `$1${tabColorXml}<pageSetUpPr fitToPage="1"/>`,
          );
        } else {
          xml = xml.replace(/(<sheetPr[^>]*>)/, `$1${tabColorXml}`);
        }
      } else {
        xml = xml.replace(
          /(<worksheet[^>]*>)/,
          `$1<sheetPr>${tabColorXml}<pageSetUpPr fitToPage="1"/></sheetPr>`,
        );
      }

      xml = xml.replace(
        /(<pageMargins[^/]*\/>)/,
        '$1<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0" paperSize="1"/>',
      );
      files[sheetPath] = strToU8(xml);
    });

  const out = zipSync(files);
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildSheet(wb, sheetData, sheetName, roleLabel) {
  const isEmployee = roleLabel.startsWith("Employee");
  const cols = COLUMNS.filter((c) => !(isEmployee && c.employeeHide));
  const NCOLS = cols.length;

  const groupSpans = [];
  cols.forEach((c, i) => {
    const last = groupSpans[groupSpans.length - 1];
    if (last && last.name === c.group) last.e = i;
    else groupSpans.push({ name: c.group, s: i, e: i });
  });

  const headers = cols.map((c) => c.header(roleLabel));
  const rows = sheetData.map((s) => cols.map((c) => c.value(s)));

  const seen = new Set();
  let storeSaleSum = 0,
    quotaSum = 0,
    storeCreditSum = 0,
    salesmanSaleSum = 0,
    salesmanCommSum = 0,
    totalCommSum = 0;
  const quotaPercents = [],
    upDownPercents = [];

  sheetData.forEach((s) => {
    if (!seen.has(s.storeId)) {
      storeSaleSum += Number(s.rawStoreSale ?? s.storeSale) || 0;
      quotaSum += Number(s.rawQuota ?? s.quota) || 0;
      if (s.quotaAchieved != null) quotaPercents.push(Number(s.quotaAchieved));
      if (s.storeUpDownPercent != null)
        upDownPercents.push(Number(s.storeUpDownPercent));
      seen.add(s.storeId);
    }
    storeCreditSum += Number(s.storeCreditAmt) || 0;
    salesmanSaleSum += Number(s.salesmanSale) || 0;
    salesmanCommSum += Number(s.salesmanCommissionAmt) || 0;
    totalCommSum += Number(s.totalCommission) || 0;
  });

  const avg = (a) =>
    a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;

  const totalByKey = {
    total: totalCommSum,
    storeSale: storeSaleSum,
    quota: quotaSum,
    quotaPct: avg(quotaPercents),
    upDown: avg(upDownPercents),
    storeComm: storeCreditSum,
    salesmanSale: salesmanSaleSum,
    salesmanComm: salesmanCommSum,
  };

  const qPct = totalByKey.quotaPct;
  let tabColor = null;
  if (qPct != null) {
    if (qPct >= QUOTA_HILITE) {
      tabColor = "FF22C55E";
    } else if (qPct <= 0) {
      tabColor = "FF94A3B8";
    }
  } else {
    tabColor = "FF94A3B8";
  }
  if (!wb.tabColors) wb.tabColors = [];
  wb.tabColors.push(tabColor);

  const totalRow = cols.map((c, i) => {
    if (i === 0) return "GRAND TOTAL";
    if (c.totalKey && totalByKey[c.totalKey] != null)
      return totalByKey[c.totalKey];
    return "";
  });

  const bandRow = Array(NCOLS).fill(null);
  groupSpans.forEach((g) => (bandRow[g.s] = g.name));
  const spacer = Array(NCOLS).fill(null);
  spacer[0] = "";

  const ws = XLSX.utils.aoa_to_sheet([
    bandRow,
    headers,
    ...rows,
    spacer,
    totalRow,
  ]);

  const N = rows.length;
  const BAND = 0,
    HEAD = 1,
    DATA0 = 2,
    DATAE = 1 + N,
    SPACER = DATAE + 1,
    TOTAL = DATAE + 2;

  const thin = { style: "thin", color: { rgb: "CBD5E1" } };
  const border = { top: thin, bottom: thin, left: thin, right: thin };

  for (let r = 0; r <= TOTAL; r++) {
    if (r === SPACER) continue;

    let dataFill = null;
    if (r >= DATA0 && r <= DATAE) {
      const src = sheetData[r - DATA0];
      const hitQuota =
        src &&
        src.quotaAchieved != null &&
        Number(src.quotaAchieved) >= QUOTA_HILITE;
      if (hitQuota) dataFill = { fgColor: { rgb: HITQUOTA } };
      else if ((r - DATA0) % 2 === 1) dataFill = { fgColor: { rgb: "F1F5F9" } };
    }

    for (let c = 0; c < NCOLS; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell) continue;
      const col = cols[c];
      const meta = GROUP_META[col.group || "Identity"];
      const st = { border };

      if (r === BAND) {
        st.fill = { fgColor: { rgb: meta.band } };
        st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.band };
        st.alignment = { horizontal: "center", vertical: "center" };
      } else if (r === HEAD) {
        st.fill = { fgColor: { rgb: meta.head } };
        st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.head };
        st.alignment = {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        };
      } else if (r === TOTAL) {
        st.fill = { fgColor: { rgb: "E2E8F0" } };
        st.font = { bold: true, color: { rgb: "0F172A" }, sz: FONT_SZ.total };
        st.border = {
          ...border,
          top: { style: "medium", color: { rgb: "94A3B8" } },
        };
        st.alignment = { horizontal: "center", vertical: "center" };
        if (typeof cell.v === "number") {
          cell.t = "n";
          if (col.type === "cur") cell.z = ACCT;
          else if (col.type === "pct") cell.z = PCT;
          if (cell.z) st.numFmt = cell.z;
        }
      } else {
        if (dataFill) st.fill = dataFill;
        if (typeof cell.v === "number") {
          cell.t = "n";
          if (col.type === "cur") cell.z = ACCT;
          else if (col.type === "pct") cell.z = PCT;
          if (cell.z) st.numFmt = cell.z;
        }
        st.font = { sz: FONT_SZ.data, bold: col.type === "cur" };
        st.alignment = col.center
          ? { horizontal: "center", vertical: "center" }
          : { vertical: "center" };
      }
      cell.s = st;
    }
  }

  ws["!merges"] = [
    ...groupSpans
      .filter((g) => g.s < g.e)
      .map((g) => ({ s: { r: BAND, c: g.s }, e: { r: BAND, c: g.e } })),
    { s: { r: SPACER, c: 0 }, e: { r: SPACER, c: NCOLS - 1 } },
    { s: { r: TOTAL, c: 0 }, e: { r: TOTAL, c: 1 } },
  ];

  ws["!cols"] = cols.map((c) => ({ wch: c.width }));

  const rowH = Array(TOTAL + 1).fill({ hpt: ROW_HPT.data });
  rowH[BAND] = { hpt: ROW_HPT.band };
  rowH[HEAD] = { hpt: ROW_HPT.head };
  rowH[SPACER] = { hpt: ROW_HPT.spacer };
  rowH[TOTAL] = { hpt: ROW_HPT.total };
  ws["!rows"] = rowH;

  ws["!margins"] = {
    left: 0.25,
    right: 0.25,
    top: 0.4,
    bottom: 0.4,
    header: 0.2,
    footer: 0.2,
  };

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
}

export function exportCommissionReportToExcel(data) {
  if (!Array.isArray(data) || data.length === 0) return;

  const roleLabel =
    data[0]?.activeTab === "Manager"
      ? "Manager"
      : data[0]?.activeTab === "Assistant Manager"
        ? "Assistant Manager"
        : "Employee";

  const wb = XLSX.utils.book_new();
  wb.tabColors = [];

  if (roleLabel === "Employee") {
    buildSheet(wb, data, "ALL", "Employee");

    const storeIds = Array.from(
      new Set(data.map((s) => Number(s.storeId))),
    ).sort((a, b) => a - b);

    storeIds.forEach((storeId) => {
      const storeData = data.filter((s) => Number(s.storeId) === storeId);
      const isEE = EE_STORES.has(storeId);
      const isGE = GE_STORES.has(storeId);
      const suffix = isEE ? "-EE" : isGE ? "-GE" : "";

      buildSheet(wb, storeData, `Store ${storeId}`, `Employee${suffix}`);
    });
  } else {
    buildSheet(wb, data, "ALL", roleLabel);
    buildSheet(
      wb,
      data.filter((s) => EE_STORES.has(Number(s.storeId))),
      "EE",
      `${roleLabel}-EE`,
    );
    buildSheet(
      wb,
      data.filter((s) => GE_STORES.has(Number(s.storeId))),
      "GE",
      `${roleLabel}-GE`,
    );
  }

  downloadLandscapeXlsx(wb, `commission_report_${Date.now()}.xlsx`);
}

// // src/components/ExcelExport.jsx
// // Requires: npm i xlsx-js-style fflate
// import * as XLSX from "xlsx-js-style";
// import { unzipSync, zipSync, strToU8, strFromU8 } from "fflate";

// // Red, minus-sign negatives (no parentheses), accounting-aligned
// const ACCT = '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';
// const PCT = "0.00%;[Red]-0.00%";
// const HITQUOTA = "DCFCE7"; // soft green — rows that hit >=99% of quota
// const QUOTA_HILITE = 0.99; // highlight threshold (quotaAchieved is a fraction)

// // ── Sizing (bigger fonts + taller rows; print stays width-fit) ──────
// const FONT_SZ = { band: 12, head: 11, data: 12, total: 12 };
// const ROW_HPT = { band: 26, head: 34, data: 22, spacer: 26, total: 24 };

// // ── Store divisions ────────────────────────────────────────────────
// const EE_STORES = new Set([1, 2, 3, 9, 15, 18, 19, 21, 22]);
// const GE_STORES = new Set([4, 5, 6, 7, 8, 10, 11, 12, 14, 16, 17, 23]);

// // ── Group colors ───────────────────────────────────────────────────
// const GROUP_META = {
//   Identity: { band: "E2E8F0", head: "F1F5F9", text: "334155" },
//   Total: { band: "FEF3C7", head: "FFFBEB", text: "92400E" },
//   "Store Performance": { band: "DBEAFE", head: "EFF6FF", text: "1E40AF" },
//   "Store Commission": { band: "EDE9FE", head: "F5F3FF", text: "5B21B6" },
//   "Salesman Commission": { band: "CFFAFE", head: "ECFEFF", text: "155E75" },
// };

// // ── Column definitions ─────────────────────────────────────────────
// // type: "cur" | "pct" | "text".  employeeHide: dropped when role is Employee.
// // totalKey: which grand-total feeds this column's footer cell.
// const COLUMNS = [
//   {
//     key: "store",
//     group: "Identity",
//     type: "text",
//     center: true,
//     width: 6,
//     header: () => "Store",
//     value: (s) => `${s.storeId}`,
//   },
//   {
//     key: "name",
//     group: "Identity",
//     type: "text",
//     width: 32,
//     header: (role) => role,
//     value: (s) => (s.name ? `${s.name} (${s.employeeId})` : s.employeeId || ""),
//   },
//   {
//     key: "total",
//     group: "Total",
//     type: "cur",
//     width: 16,
//     totalKey: "total",
//     header: () => "Total Comm.",
//     value: (s) => Number(s.totalCommission) || 0,
//   },
//   {
//     key: "storeSale",
//     group: "Store Performance",
//     type: "cur",
//     width: 18,
//     totalKey: "storeSale",
//     header: () => "Store Sales",
//     value: (s) => Number(s.storeSale) || 0,
//   },
//   {
//     key: "quota",
//     group: "Store Performance",
//     type: "cur",
//     width: 16,
//     totalKey: "quota",
//     header: () => "Quota",
//     value: (s) => Number(s.quota) || 0,
//   },
//   {
//     key: "quotaPct",
//     group: "Store Performance",
//     type: "pct",
//     center: true,
//     width: 12,
//     totalKey: "quotaPct",
//     header: () => "Quota %",
//     value: (s) => (s.quotaAchieved != null ? Number(s.quotaAchieved) : null),
//   },
//   {
//     key: "upDown",
//     group: "Store Performance",
//     type: "pct",
//     center: true,
//     width: 12,
//     totalKey: "upDown",
//     header: () => "Up/Down %",
//     value: (s) =>
//       s.storeUpDownPercent != null ? Number(s.storeUpDownPercent) : null,
//   },
//   {
//     key: "storeRate",
//     group: "Store Commission",
//     type: "pct",
//     center: true,
//     width: 14,
//     employeeHide: true,
//     header: () => "Store Credit Rate",
//     value: (s) => Number(s.storeCreditRate) || 0,
//   },
//   {
//     key: "storeComm",
//     group: "Store Commission",
//     type: "cur",
//     width: 16,
//     employeeHide: true,
//     totalKey: "storeComm",
//     header: () => "Store Comm.",
//     value: (s) => Number(s.storeCreditAmt) || 0,
//   },
//   {
//     key: "salesmanSale",
//     group: "Salesman Commission",
//     type: "cur",
//     width: 18,
//     totalKey: "salesmanSale",
//     header: () => "Salesman Sales",
//     value: (s) => Number(s.salesmanSale) || 0,
//   },
//   {
//     key: "salesmanRate",
//     group: "Salesman Commission",
//     type: "pct",
//     center: true,
//     width: 13,
//     header: () => "Salesman Rate",
//     value: (s) => Number(s.salesmanRate) || 0,
//   },
//   {
//     key: "salesmanComm",
//     group: "Salesman Commission",
//     type: "cur",
//     width: 18,
//     totalKey: "salesmanComm",
//     header: () => "Salesman Comm.",
//     value: (s) => Number(s.salesmanCommissionAmt) || 0,
//   },
// ];

// // Write the workbook, then patch EVERY sheet's XML for landscape print and tab color.
// function downloadLandscapeXlsx(wb, filename) {
//   const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//   const files = unzipSync(new Uint8Array(buf));

//   Object.keys(files)
//     .filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k))
//     .forEach((sheetPath) => {
//       const match = sheetPath.match(/^xl\/worksheets\/sheet(\d+)\.xml$/);
//       let tabColor = null;
//       if (match && wb.tabColors) {
//         const idx = parseInt(match[1], 10) - 1;
//         tabColor = wb.tabColors[idx];
//       }

//       let xml = strFromU8(files[sheetPath]);
//       const tabColorXml = tabColor ? `<tabColor rgb="${tabColor}"/>` : "";

//       // Safe injector that accounts for whether sheetPr already exists in the file structure
//       if (xml.includes("<sheetPr")) {
//         if (!xml.includes("pageSetUpPr")) {
//           xml = xml.replace(
//             /(<sheetPr[^>]*>)/,
//             `$1${tabColorXml}<pageSetUpPr fitToPage="1"/>`,
//           );
//         } else {
//           xml = xml.replace(/(<sheetPr[^>]*>)/, `$1${tabColorXml}`);
//         }
//       } else {
//         xml = xml.replace(
//           /(<worksheet[^>]*>)/,
//           `$1<sheetPr>${tabColorXml}<pageSetUpPr fitToPage="1"/></sheetPr>`,
//         );
//       }

//       xml = xml.replace(
//         /(<pageMargins[^/]*\/>)/,
//         '$1<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0" paperSize="1"/>',
//       );
//       files[sheetPath] = strToU8(xml);
//     });

//   const out = zipSync(files);
//   const blob = new Blob([out], {
//     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//   });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = filename;
//   a.click();
//   URL.revokeObjectURL(url);
// }

// // Build one styled worksheet from a slice of rows and append it to the workbook.
// function buildSheet(wb, sheetData, sheetName, roleLabel) {
//   const isEmployee = roleLabel.startsWith("Employee");
//   // Drop the Store Commission columns on the Employee view
//   const cols = COLUMNS.filter((c) => !(isEmployee && c.employeeHide));
//   const NCOLS = cols.length;

//   // Contiguous group spans (for band labels + merges)
//   const groupSpans = [];
//   cols.forEach((c, i) => {
//     const last = groupSpans[groupSpans.length - 1];
//     if (last && last.name === c.group) last.e = i;
//     else groupSpans.push({ name: c.group, s: i, e: i });
//   });

//   const headers = cols.map((c) => c.header(roleLabel));
//   const rows = sheetData.map((s) => cols.map((c) => c.value(s)));

//   /* ---- grand totals (raw store figures, de-duped by storeId) ---- */
//   const seen = new Set();
//   let storeSaleSum = 0,
//     quotaSum = 0,
//     storeCreditSum = 0,
//     salesmanSaleSum = 0,
//     salesmanCommSum = 0,
//     totalCommSum = 0;
//   const quotaPercents = [],
//     upDownPercents = [];

//   sheetData.forEach((s) => {
//     if (!seen.has(s.storeId)) {
//       storeSaleSum += Number(s.rawStoreSale ?? s.storeSale) || 0;
//       quotaSum += Number(s.rawQuota ?? s.quota) || 0;
//       if (s.quotaAchieved != null) quotaPercents.push(Number(s.quotaAchieved));
//       if (s.storeUpDownPercent != null)
//         upDownPercents.push(Number(s.storeUpDownPercent));
//       seen.add(s.storeId);
//     }
//     storeCreditSum += Number(s.storeCreditAmt) || 0;
//     salesmanSaleSum += Number(s.salesmanSale) || 0;
//     salesmanCommSum += Number(s.salesmanCommissionAmt) || 0;
//     totalCommSum += Number(s.totalCommission) || 0;
//   });

//   const avg = (a) =>
//     a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;

//   const totalByKey = {
//     total: totalCommSum,
//     storeSale: storeSaleSum,
//     quota: quotaSum,
//     quotaPct: avg(quotaPercents),
//     upDown: avg(upDownPercents),
//     storeComm: storeCreditSum,
//     salesmanSale: salesmanSaleSum,
//     salesmanComm: salesmanCommSum,
//   };

//   // Determine Sheet Tab Color based on Quota Performance
//   const qPct = totalByKey.quotaPct;
//   let tabColor = null;
//   if (qPct != null) {
//     if (qPct >= QUOTA_HILITE) {
//       tabColor = "FF22C55E"; // Clean solid green when quota threshold met
//     } else if (qPct <= 0) {
//       tabColor = "FF94A3B8"; // Slate gray when absolute zero/missing performance
//     }
//   } else {
//     tabColor = "FF94A3B8"; // Fallback gray
//   }
//   if (!wb.tabColors) wb.tabColors = [];
//   wb.tabColors.push(tabColor);

//   const totalRow = cols.map((c, i) => {
//     if (i === 0) return "GRAND TOTAL";
//     if (c.totalKey && totalByKey[c.totalKey] != null)
//       return totalByKey[c.totalKey];
//     return "";
//   });

//   /* ---- layout: band row, header row, data, merged gap, total ---- */
//   const bandRow = Array(NCOLS).fill(null);
//   groupSpans.forEach((g) => (bandRow[g.s] = g.name));
//   const spacer = Array(NCOLS).fill(null);
//   spacer[0] = ""; // anchor cell for the merged gap

//   const ws = XLSX.utils.aoa_to_sheet([
//     bandRow,
//     headers,
//     ...rows,
//     spacer,
//     totalRow,
//   ]);

//   const N = rows.length;
//   const BAND = 0,
//     HEAD = 1,
//     DATA0 = 2,
//     DATAE = 1 + N,
//     SPACER = DATAE + 1,
//     TOTAL = DATAE + 2;

//   const thin = { style: "thin", color: { rgb: "CBD5E1" } };
//   const border = { top: thin, bottom: thin, left: thin, right: thin };

//   for (let r = 0; r <= TOTAL; r++) {
//     if (r === SPACER) continue;

//     // per-row data fill: green if the store hit >=99% of quota, else zebra
//     let dataFill = null;
//     if (r >= DATA0 && r <= DATAE) {
//       const src = sheetData[r - DATA0];
//       const hitQuota =
//         src &&
//         src.quotaAchieved != null &&
//         Number(src.quotaAchieved) >= QUOTA_HILITE;
//       if (hitQuota) dataFill = { fgColor: { rgb: HITQUOTA } };
//       else if ((r - DATA0) % 2 === 1) dataFill = { fgColor: { rgb: "F1F5F9" } };
//     }

//     for (let c = 0; c < NCOLS; c++) {
//       const cell = ws[XLSX.utils.encode_cell({ r, c })];
//       if (!cell) continue;
//       const col = cols[c];
//       const meta = GROUP_META[col.group || "Identity"];
//       const st = { border };

//       if (r === BAND) {
//         st.fill = { fgColor: { rgb: meta.band } };
//         st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.band };
//         st.alignment = { horizontal: "center", vertical: "center" };
//       } else if (r === HEAD) {
//         st.fill = { fgColor: { rgb: meta.head } };
//         st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.head };
//         st.alignment = {
//           horizontal: "center",
//           vertical: "center",
//           wrapText: true,
//         };
//       } else if (r === TOTAL) {
//         st.fill = { fgColor: { rgb: "E2E8F0" } };
//         st.font = { bold: true, color: { rgb: "0F172A" }, sz: FONT_SZ.total };
//         st.border = {
//           ...border,
//           top: { style: "medium", color: { rgb: "94A3B8" } },
//         };
//         st.alignment = { horizontal: "center", vertical: "center" };
//         if (typeof cell.v === "number") {
//           cell.t = "n";
//           if (col.type === "cur") cell.z = ACCT;
//           else if (col.type === "pct") cell.z = PCT;
//           if (cell.z) st.numFmt = cell.z;
//         }
//       } else {
//         // data rows — quota-hit highlight or zebra
//         if (dataFill) st.fill = dataFill;
//         if (typeof cell.v === "number") {
//           cell.t = "n";
//           if (col.type === "cur") cell.z = ACCT;
//           else if (col.type === "pct") cell.z = PCT;
//           if (cell.z) st.numFmt = cell.z;
//         }
//         st.font = { sz: FONT_SZ.data, bold: col.type === "cur" };
//         st.alignment = col.center
//           ? { horizontal: "center", vertical: "center" }
//           : { vertical: "center" };
//       }
//       cell.s = st;
//     }
//   }

//   /* ---- merges, widths, row heights, margins ---- */
//   ws["!merges"] = [
//     ...groupSpans
//       .filter((g) => g.s < g.e)
//       .map((g) => ({ s: { r: BAND, c: g.s }, e: { r: BAND, c: g.e } })),
//     { s: { r: SPACER, c: 0 }, e: { r: SPACER, c: NCOLS - 1 } }, // merged gap
//     { s: { r: TOTAL, c: 0 }, e: { r: TOTAL, c: 1 } }, // GRAND TOTAL label
//   ];

//   ws["!cols"] = cols.map((c) => ({ wch: c.width }));

//   const rowH = Array(TOTAL + 1).fill({ hpt: ROW_HPT.data });
//   rowH[BAND] = { hpt: ROW_HPT.band };
//   rowH[HEAD] = { hpt: ROW_HPT.head }; // taller for wrapped headers
//   rowH[SPACER] = { hpt: ROW_HPT.spacer }; // single, taller, merged gap
//   rowH[TOTAL] = { hpt: ROW_HPT.total };
//   ws["!rows"] = rowH;

//   ws["!margins"] = {
//     left: 0.25,
//     right: 0.25,
//     top: 0.4,
//     bottom: 0.4,
//     header: 0.2,
//     footer: 0.2,
//   };

//   XLSX.utils.book_append_sheet(wb, ws, sheetName);
// }

// export function exportCommissionReportToExcel(data) {
//   if (!Array.isArray(data) || data.length === 0) return;

//   const roleLabel =
//     data[0]?.activeTab === "Manager"
//       ? "Manager"
//       : data[0]?.activeTab === "Assistant Manager"
//         ? "Assistant Manager"
//         : "Employee";

//   const wb = XLSX.utils.book_new();
//   wb.tabColors = [];

//   if (roleLabel === "Employee") {
//     // Tab 1: Everything consolidated
//     buildSheet(wb, data, "ALL", "Employee");

//     // Dynamic distinct stores sorted numerically
//     const storeIds = Array.from(
//       new Set(data.map((s) => Number(s.storeId))),
//     ).sort((a, b) => a - b);

//     storeIds.forEach((storeId) => {
//       const storeData = data.filter((s) => Number(s.storeId) === storeId);
//       const isEE = EE_STORES.has(storeId);
//       const isGE = GE_STORES.has(storeId);
//       const suffix = isEE ? "-EE" : isGE ? "-GE" : "";

//       // Pass modified string to evaluate specific headers (Employee-EE / Employee-GE)
//       buildSheet(wb, storeData, `Store ${storeId}`, `Employee${suffix}`);
//     });
//   } else {
//     // Keep original regional layout logic intact for Manager / Assistant Manager active records
//     buildSheet(wb, data, "ALL", roleLabel);
//     buildSheet(
//       wb,
//       data.filter((s) => EE_STORES.has(Number(s.storeId))),
//       "EE",
//       `${roleLabel}-EE`,
//     );
//     buildSheet(
//       wb,
//       data.filter((s) => GE_STORES.has(Number(s.storeId))),
//       "GE",
//       `${roleLabel}-GE`,
//     );
//   }

//   downloadLandscapeXlsx(wb, `commission_report_${Date.now()}.xlsx`);
// }

// // // src/components/ExcelExport.jsx
// // // Requires: npm i xlsx-js-style fflate
// // import * as XLSX from "xlsx-js-style";
// // import { unzipSync, zipSync, strToU8, strFromU8 } from "fflate";

// // // Red, minus-sign negatives (no parentheses), accounting-aligned
// // const ACCT = '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';
// // const PCT = "0.00%;[Red]-0.00%";
// // const HITQUOTA = "DCFCE7"; // soft green — rows that hit >=99% of quota
// // const QUOTA_HILITE = 0.99; // highlight threshold (quotaAchieved is a fraction)

// // // ── Sizing (bigger fonts + taller rows; print stays width-fit) ──────
// // const FONT_SZ = { band: 12, head: 11, data: 12, total: 12 };
// // const ROW_HPT = { band: 26, head: 34, data: 22, spacer: 26, total: 24 };

// // // ── Store divisions ────────────────────────────────────────────────
// // const EE_STORES = new Set([1, 2, 3, 9, 15, 18, 19, 21, 22]);
// // const GE_STORES = new Set([4, 5, 6, 7, 8, 10, 11, 12, 14, 16, 17, 23]);

// // // ── Group colors ───────────────────────────────────────────────────
// // const GROUP_META = {
// //   Identity: { band: "E2E8F0", head: "F1F5F9", text: "334155" },
// //   Total: { band: "FEF3C7", head: "FFFBEB", text: "92400E" },
// //   "Store Performance": { band: "DBEAFE", head: "EFF6FF", text: "1E40AF" },
// //   "Store Commission": { band: "EDE9FE", head: "F5F3FF", text: "5B21B6" },
// //   "Salesman Commission": { band: "CFFAFE", head: "ECFEFF", text: "155E75" },
// // };

// // // ── Column definitions ─────────────────────────────────────────────
// // // type: "cur" | "pct" | "text".  employeeHide: dropped when role is Employee.
// // // totalKey: which grand-total feeds this column's footer cell.
// // const COLUMNS = [
// //   {
// //     key: "store",
// //     group: "Identity",
// //     type: "text",
// //     center: true,
// //     width: 6,
// //     header: () => "Store",
// //     value: (s) => `${s.storeId}`,
// //   },
// //   {
// //     key: "name",
// //     group: "Identity",
// //     type: "text",
// //     width: 26,
// //     header: (role) => role,
// //     value: (s) => (s.name ? `${s.name} (${s.employeeId})` : s.employeeId || ""),
// //   },
// //   {
// //     key: "total",
// //     group: "Total",
// //     type: "cur",
// //     width: 16,
// //     totalKey: "total",
// //     header: () => "Total Comm.",
// //     value: (s) => Number(s.totalCommission) || 0,
// //   },
// //   {
// //     key: "storeSale",
// //     group: "Store Performance",
// //     type: "cur",
// //     width: 18,
// //     totalKey: "storeSale",
// //     header: () => "Store Sales",
// //     value: (s) => Number(s.storeSale) || 0,
// //   },
// //   {
// //     key: "quota",
// //     group: "Store Performance",
// //     type: "cur",
// //     width: 16,
// //     totalKey: "quota",
// //     header: () => "Quota",
// //     value: (s) => Number(s.quota) || 0,
// //   },
// //   {
// //     key: "quotaPct",
// //     group: "Store Performance",
// //     type: "pct",
// //     center: true,
// //     width: 12,
// //     totalKey: "quotaPct",
// //     header: () => "Quota %",
// //     value: (s) => (s.quotaAchieved != null ? Number(s.quotaAchieved) : null),
// //   },
// //   {
// //     key: "upDown",
// //     group: "Store Performance",
// //     type: "pct",
// //     center: true,
// //     width: 12,
// //     totalKey: "upDown",
// //     header: () => "Up/Down %",
// //     value: (s) =>
// //       s.storeUpDownPercent != null ? Number(s.storeUpDownPercent) : null,
// //   },
// //   {
// //     key: "storeRate",
// //     group: "Store Commission",
// //     type: "pct",
// //     center: true,
// //     width: 14,
// //     employeeHide: true,
// //     header: () => "Store Credit Rate",
// //     value: (s) => Number(s.storeCreditRate) || 0,
// //   },
// //   {
// //     key: "storeComm",
// //     group: "Store Commission",
// //     type: "cur",
// //     width: 16,
// //     employeeHide: true,
// //     totalKey: "storeComm",
// //     header: () => "Store Comm.",
// //     value: (s) => Number(s.storeCreditAmt) || 0,
// //   },
// //   {
// //     key: "salesmanSale",
// //     group: "Salesman Commission",
// //     type: "cur",
// //     width: 18,
// //     totalKey: "salesmanSale",
// //     header: () => "Salesman Sales",
// //     value: (s) => Number(s.salesmanSale) || 0,
// //   },
// //   {
// //     key: "salesmanRate",
// //     group: "Salesman Commission",
// //     type: "pct",
// //     center: true,
// //     width: 13,
// //     header: () => "Salesman Rate",
// //     value: (s) => Number(s.salesmanRate) || 0,
// //   },
// //   {
// //     key: "salesmanComm",
// //     group: "Salesman Commission",
// //     type: "cur",
// //     width: 18,
// //     totalKey: "salesmanComm",
// //     header: () => "Salesman Comm.",
// //     value: (s) => Number(s.salesmanCommissionAmt) || 0,
// //   },
// // ];

// // // Write the workbook, then patch EVERY sheet's XML for landscape print.
// // function downloadLandscapeXlsx(wb, filename) {
// //   const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
// //   const files = unzipSync(new Uint8Array(buf));

// //   Object.keys(files)
// //     .filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k))
// //     .forEach((sheetPath) => {
// //       let xml = strFromU8(files[sheetPath]);
// //       xml = xml.replace(
// //         /(<worksheet[^>]*>)/,
// //         '$1<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>',
// //       );
// //       xml = xml.replace(
// //         /(<pageMargins[^/]*\/>)/,
// //         '$1<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0" paperSize="1"/>',
// //       );
// //       files[sheetPath] = strToU8(xml);
// //     });

// //   const out = zipSync(files);
// //   const blob = new Blob([out], {
// //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
// //   });
// //   const url = URL.createObjectURL(blob);
// //   const a = document.createElement("a");
// //   a.href = url;
// //   a.download = filename;
// //   a.click();
// //   URL.revokeObjectURL(url);
// // }

// // // Build one styled worksheet from a slice of rows and append it to the workbook.
// // function buildSheet(wb, sheetData, sheetName, roleLabel) {
// //   const isEmployee = roleLabel === "Employee";
// //   // Drop the Store Commission columns on the Employee view
// //   const cols = COLUMNS.filter((c) => !(isEmployee && c.employeeHide));
// //   const NCOLS = cols.length;

// //   // Contiguous group spans (for band labels + merges)
// //   const groupSpans = [];
// //   cols.forEach((c, i) => {
// //     const last = groupSpans[groupSpans.length - 1];
// //     if (last && last.name === c.group) last.e = i;
// //     else groupSpans.push({ name: c.group, s: i, e: i });
// //   });

// //   const headers = cols.map((c) => c.header(roleLabel));
// //   const rows = sheetData.map((s) => cols.map((c) => c.value(s)));

// //   /* ---- grand totals (raw store figures, de-duped by storeId) ---- */
// //   const seen = new Set();
// //   let storeSaleSum = 0,
// //     quotaSum = 0,
// //     storeCreditSum = 0,
// //     salesmanSaleSum = 0,
// //     salesmanCommSum = 0,
// //     totalCommSum = 0;
// //   const quotaPercents = [],
// //     upDownPercents = [];

// //   sheetData.forEach((s) => {
// //     if (!seen.has(s.storeId)) {
// //       storeSaleSum += Number(s.rawStoreSale ?? s.storeSale) || 0;
// //       quotaSum += Number(s.rawQuota ?? s.quota) || 0;
// //       if (s.quotaAchieved != null) quotaPercents.push(Number(s.quotaAchieved));
// //       if (s.storeUpDownPercent != null)
// //         upDownPercents.push(Number(s.storeUpDownPercent));
// //       seen.add(s.storeId);
// //     }
// //     storeCreditSum += Number(s.storeCreditAmt) || 0;
// //     salesmanSaleSum += Number(s.salesmanSale) || 0;
// //     salesmanCommSum += Number(s.salesmanCommissionAmt) || 0;
// //     totalCommSum += Number(s.totalCommission) || 0;
// //   });

// //   const avg = (a) =>
// //     a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;

// //   const totalByKey = {
// //     total: totalCommSum,
// //     storeSale: storeSaleSum,
// //     quota: quotaSum,
// //     quotaPct: avg(quotaPercents),
// //     upDown: avg(upDownPercents),
// //     storeComm: storeCreditSum,
// //     salesmanSale: salesmanSaleSum,
// //     salesmanComm: salesmanCommSum,
// //   };

// //   const totalRow = cols.map((c, i) => {
// //     if (i === 0) return "GRAND TOTAL";
// //     if (c.totalKey && totalByKey[c.totalKey] != null)
// //       return totalByKey[c.totalKey];
// //     return "";
// //   });

// //   /* ---- layout: band row, header row, data, merged gap, total ---- */
// //   const bandRow = Array(NCOLS).fill(null);
// //   groupSpans.forEach((g) => (bandRow[g.s] = g.name));
// //   const spacer = Array(NCOLS).fill(null);
// //   spacer[0] = ""; // anchor cell for the merged gap

// //   const ws = XLSX.utils.aoa_to_sheet([
// //     bandRow,
// //     headers,
// //     ...rows,
// //     spacer,
// //     totalRow,
// //   ]);

// //   const N = rows.length;
// //   const BAND = 0,
// //     HEAD = 1,
// //     DATA0 = 2,
// //     DATAE = 1 + N,
// //     SPACER = DATAE + 1,
// //     TOTAL = DATAE + 2;

// //   const thin = { style: "thin", color: { rgb: "CBD5E1" } };
// //   const border = { top: thin, bottom: thin, left: thin, right: thin };

// //   for (let r = 0; r <= TOTAL; r++) {
// //     if (r === SPACER) continue;

// //     // per-row data fill: green if the store hit >=99% of quota, else zebra
// //     let dataFill = null;
// //     if (r >= DATA0 && r <= DATAE) {
// //       const src = sheetData[r - DATA0];
// //       const hitQuota =
// //         src &&
// //         src.quotaAchieved != null &&
// //         Number(src.quotaAchieved) >= QUOTA_HILITE;
// //       if (hitQuota) dataFill = { fgColor: { rgb: HITQUOTA } };
// //       else if ((r - DATA0) % 2 === 1) dataFill = { fgColor: { rgb: "F1F5F9" } };
// //     }

// //     for (let c = 0; c < NCOLS; c++) {
// //       const cell = ws[XLSX.utils.encode_cell({ r, c })];
// //       if (!cell) continue;
// //       const col = cols[c];
// //       const meta = GROUP_META[col.group];
// //       const st = { border };

// //       if (r === BAND) {
// //         st.fill = { fgColor: { rgb: meta.band } };
// //         st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.band };
// //         st.alignment = { horizontal: "center", vertical: "center" };
// //       } else if (r === HEAD) {
// //         st.fill = { fgColor: { rgb: meta.head } };
// //         st.font = { bold: true, color: { rgb: meta.text }, sz: FONT_SZ.head };
// //         st.alignment = {
// //           horizontal: "center",
// //           vertical: "center",
// //           wrapText: true,
// //         };
// //       } else if (r === TOTAL) {
// //         st.fill = { fgColor: { rgb: "E2E8F0" } };
// //         st.font = { bold: true, color: { rgb: "0F172A" }, sz: FONT_SZ.total };
// //         st.border = {
// //           ...border,
// //           top: { style: "medium", color: { rgb: "94A3B8" } },
// //         };
// //         st.alignment = { horizontal: "center", vertical: "center" };
// //         if (typeof cell.v === "number") {
// //           cell.t = "n";
// //           if (col.type === "cur") cell.z = ACCT;
// //           else if (col.type === "pct") cell.z = PCT;
// //           if (cell.z) st.numFmt = cell.z;
// //         }
// //       } else {
// //         // data rows — quota-hit highlight or zebra
// //         if (dataFill) st.fill = dataFill;
// //         if (typeof cell.v === "number") {
// //           cell.t = "n";
// //           if (col.type === "cur") cell.z = ACCT;
// //           else if (col.type === "pct") cell.z = PCT;
// //           if (cell.z) st.numFmt = cell.z;
// //         }
// //         st.font = { sz: FONT_SZ.data, bold: col.type === "cur" };
// //         st.alignment = col.center
// //           ? { horizontal: "center", vertical: "center" }
// //           : { vertical: "center" };
// //       }
// //       cell.s = st;
// //     }
// //   }

// //   /* ---- merges, widths, row heights, margins ---- */
// //   ws["!merges"] = [
// //     ...groupSpans
// //       .filter((g) => g.s < g.e)
// //       .map((g) => ({ s: { r: BAND, c: g.s }, e: { r: BAND, c: g.e } })),
// //     { s: { r: SPACER, c: 0 }, e: { r: SPACER, c: NCOLS - 1 } }, // merged gap
// //     { s: { r: TOTAL, c: 0 }, e: { r: TOTAL, c: 1 } }, // GRAND TOTAL label
// //   ];

// //   ws["!cols"] = cols.map((c) => ({ wch: c.width }));

// //   const rowH = Array(TOTAL + 1).fill({ hpt: ROW_HPT.data });
// //   rowH[BAND] = { hpt: ROW_HPT.band };
// //   rowH[HEAD] = { hpt: ROW_HPT.head }; // taller for wrapped headers
// //   rowH[SPACER] = { hpt: ROW_HPT.spacer }; // single, taller, merged gap
// //   rowH[TOTAL] = { hpt: ROW_HPT.total };
// //   ws["!rows"] = rowH;

// //   ws["!margins"] = {
// //     left: 0.25,
// //     right: 0.25,
// //     top: 0.4,
// //     bottom: 0.4,
// //     header: 0.2,
// //     footer: 0.2,
// //   };

// //   XLSX.utils.book_append_sheet(wb, ws, sheetName);
// // }

// // export function exportCommissionReportToExcel(data) {
// //   if (!Array.isArray(data) || data.length === 0) return;

// //   const roleLabel =
// //     data[0]?.activeTab === "Manager"
// //       ? "Manager"
// //       : data[0]?.activeTab === "Assistant Manager"
// //         ? "Assistant Manager"
// //         : "Employee";

// //   const wb = XLSX.utils.book_new();

// //   // Three tabs: everything, then the EE and GE store divisions
// //   buildSheet(wb, data, "ALL", roleLabel);
// //   buildSheet(
// //     wb,
// //     data.filter((s) => EE_STORES.has(Number(s.storeId))),
// //     "EE",
// //     roleLabel,
// //   );
// //   buildSheet(
// //     wb,
// //     data.filter((s) => GE_STORES.has(Number(s.storeId))),
// //     "GE",
// //     roleLabel,
// //   );

// //   downloadLandscapeXlsx(wb, `commission_report_${Date.now()}.xlsx`);
// // }
